import React from 'react';

import { LayoutRenderer, RowCollectionLayout } from './types';
import { sanitizeLayoutClass } from './utils';

const createGridStubLayout = (layoutType: RowCollectionLayout, slots: number): LayoutRenderer => {
  const GridLayout: LayoutRenderer = ({ children }) => {
    const nodes = React.Children.toArray(children).filter(Boolean).slice(0, slots);
    const className = `itv-row-collection__grid itv-row-collection__grid--${sanitizeLayoutClass(layoutType)}`;

    return (
      <div className={className}>
        {nodes.map((tile, index) => (
          <div key={`${layoutType}-grid-cell-${index}`} className="itv-row-collection__grid-cell">
            {tile}
          </div>
        ))}
      </div>
    );
  };

  GridLayout.displayName = `GridStubLayout(${layoutType})`;

  return GridLayout;
};

export const RowThreeLayout = createGridStubLayout('row-3', 3);
export const RowFiveLayout = createGridStubLayout('row-5', 5);
export const RowTwoLayout = createGridStubLayout('row-6', 2);
export const RowFourLayout = createGridStubLayout('row-6', 4);
export const RowSixLayout = createGridStubLayout('row-6', 6);
export const GridSixLayout = createGridStubLayout('grid-6', 6);

const TALL_GROUP_SIZE = 5;
const TALL_GRID_SIZE = 4;
const TALL_GROUP_COUNT = 2;

const GRID_TWO_BY_TWO_GROUP_SIZE = 4;
const GRID_TWO_BY_TWO_GROUP_COUNT = 2;

export const GridTwoByTwoLayout: LayoutRenderer = ({ children }) => {
  const nodes = React.Children.toArray(children).filter(Boolean);
  const groups = Array.from({ length: GRID_TWO_BY_TWO_GROUP_COUNT }, (_, groupIndex) =>
    nodes.slice(groupIndex * GRID_TWO_BY_TWO_GROUP_SIZE, (groupIndex + 1) * GRID_TWO_BY_TWO_GROUP_SIZE)
  ).filter((group) => group.length > 0);

  return (
    <div className="itv-row-collection__double-grid">
      {groups.map((groupNodes, groupIndex) => {
        return (
          <div className="itv-row-collection__double-grid-group" key={`double-grid-group-${groupIndex}`}>
            {groupNodes.map((tile, index) => (
              <div key={`double-grid-cell-${groupIndex}-${index}`} className="itv-row-collection__double-grid-cell">
                {tile}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

export const TallGridTwoByTwoLayout: LayoutRenderer = ({ children }) => {
  const nodes = React.Children.toArray(children).filter(Boolean);

  // Mobile/Tablet layout: Row structure
  // Row 1: 1 Tall + 2 Box
  const row1Tall = nodes[0];
  const row1Boxes = nodes.slice(1, 3);

  // Row 2: 2 Box + 1 Tall
  const row2Boxes = nodes.slice(3, 5);
  const row2Tall = nodes[5];

  // Row 3: 4 Box items
  const row3Boxes = nodes.slice(6, 10);

  // Desktop layout: Original grouped structure
  const groups = Array.from({ length: TALL_GROUP_COUNT }, (_, groupIndex) => {
    const start = groupIndex * TALL_GROUP_SIZE;

    return nodes.slice(start, start + TALL_GROUP_SIZE);
  }).filter((group) => group.length > 0);

  return (
    <>
      {/* Mobile/Tablet Layout */}
      <div className="itv-row-collection__tall-grid-new">
        {/* Row 1: Tall + 2 Box */}
        <div className="itv-row-collection__tall-grid-row">
          <div className="itv-row-collection__tall-grid-cell">{row1Tall}</div>
          <div className="itv-row-collection__tall-grid-boxes">
            {row1Boxes.map((tile, index) => (
              <div key={`row1-box-${index}`} className="itv-row-collection__tall-grid-cell">
                {tile}
              </div>
            ))}
          </div>
        </div>

        {/* Row 2: 2 Box + Tall */}
        <div className="itv-row-collection__tall-grid-row">
          <div className="itv-row-collection__tall-grid-boxes">
            {row2Boxes.map((tile, index) => (
              <div key={`row2-box-${index}`} className="itv-row-collection__tall-grid-cell">
                {tile}
              </div>
            ))}
          </div>
          <div className="itv-row-collection__tall-grid-cell">{row2Tall}</div>
        </div>

        {/* Row 3: 4 Box items */}
        <div className="itv-row-collection__tall-grid-bottom">
          {row3Boxes.map((tile, index) => (
            <div key={`row3-box-${index}`} className="itv-row-collection__tall-grid-cell">
              {tile}
            </div>
          ))}
        </div>
      </div>

      {/* Desktop Layout - Original */}
      <div className="itv-row-collection__tall-grid">
        {groups.map((groupNodes, groupIndex) => {
          const [hero, ...rest] = groupNodes;

          if (!hero) return null;

          const secondaryTiles = rest.slice(0, TALL_GRID_SIZE);

          return (
            <div className="itv-row-collection__tall-grid-group" key={`tall-grid-group-${groupIndex}`}>
              <div className="itv-row-collection__tall-grid-primary">{hero}</div>
              <div className="itv-row-collection__tall-grid-secondary">
                {secondaryTiles.map((tile, index) => (
                  <div
                    key={`tall-grid-secondary-cell-${groupIndex}-${index}`}
                    className="itv-row-collection__tall-grid-cell"
                  >
                    {tile}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export const HeroDoubleTallGridLayout: LayoutRenderer = ({ children }) => {
  const nodes = React.Children.toArray(children).filter(Boolean);
  const [hero, ...rest] = nodes;

  if (!hero) return null;

  const tallTiles = rest.slice(0, 2);
  const boxTiles = rest.slice(2, 6);

  return (
    <div className="itv-row-collection__hero-double-tall-grid">
      {/* Column 1: Hero */}
      <div className="itv-row-collection__hero-double-tall-grid-hero">{hero}</div>

      {/* Column 2: Tall tiles */}
      <div className="itv-row-collection__hero-double-tall-grid-tall-section">
        {tallTiles.map((tile, index) => (
          <div
            key={`hero-double-tall-grid-tall-cell-${index}`}
            className="itv-row-collection__hero-double-tall-grid-cell"
          >
            {tile}
          </div>
        ))}
      </div>

      {/* Column 3: Box tiles in 2x2 grid */}
      <div className="itv-row-collection__hero-double-tall-grid-box-section">
        {boxTiles.map((tile, index) => (
          <div
            key={`hero-double-tall-grid-box-cell-${index}`}
            className="itv-row-collection__hero-double-tall-grid-cell"
          >
            {tile}
          </div>
        ))}
      </div>
    </div>
  );
};
